import { get, kebabCase, omit, pickBy, set } from 'lodash/fp';
import { v4 as uuid } from 'uuid';

import type {
  CombinedStepForTree,
  LinkedWorkflowStep,
  TemplateStepPreview,
  VerificationTemplateVersionMap,
  WorkflowVersionMap,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import {
  AddStepConfig,
  DeleteStepConfig,
  FlowTreeData,
  FlowTreeModuleAttributes,
  NewModuleConfig,
  NewModuleOutgoing,
  NewStep,
  NodeWrapperAttributes,
  OmitUpdateConfig,
  ScreenStepConfig,
  UpdatedStepsPreviewsWorkflowMap,
  UpdatedStepsPreviewsWorkflows,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateFlowEditor/types';
import { getDisplayNameForStep } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/utils';
import {
  stepsToStepTargets,
  stepsVtmplvIds,
  stepsWorkflowErrorTargets,
  stepsWorkflowVersionTargets,
  uiStepAllComponentConfigs,
} from 'dashboard/apps/InquiryTemplateEditor/utils/steps';
import {
  stepBackTransitionUpdated,
  stepTransitionUpdated,
  stepWithClearedTransitions,
  stepWithUpdatedNames,
  stepWithUpdatedStatuses,
  uiStepPreviewTerminalUpdated,
  uiStepPreviewWithClearedTransitions,
  uiStepTerminalUpdated,
} from 'dashboard/apps/InquiryTemplateEditor/utils/steps-update';
import {
  getAllWorkflowConditionalSteps,
  isUpdateInquiryNextStepActionStep,
  workflowNextStepNames,
  workflowVtmplvIds,
} from 'dashboard/apps/InquiryTemplateEditor/utils/workflow-steps';
import {
  workflowStepsNextUpdated,
  workflowStepsUpdated,
} from 'dashboard/apps/InquiryTemplateEditor/utils/workflow-steps-update';
import {
  createBlankUiStep,
  createBlankUiStepPreview,
  createTransitionComponent,
  createTransitionComponentPreview,
} from 'dashboard/lib/stubs/inquiry-template-version';
import {
  createMockActionStep,
  createMockConditionalStep,
} from 'dashboard/lib/stubs/inquiry-workflow-version';
import { createMockVerificationTemplateVersion } from 'dashboard/lib/stubs/verification-template-version';
import { createMockWorkflowVersionV2 } from 'dashboard/lib/stubs/workflow-version';
import {
  ModuleConnectionType,
  ModuleFieldSchema,
  ModuleScreen,
  ModuleVerificationTemplateVersion,
  ModuleWorkflowVersion,
} from 'dashboard/models/inquiry-template-module';
import {
  InquiryTemplateVersionStep,
  InquiryTemplateVersionStepComplete,
  InquiryTemplateVersionStepStatus,
  InquiryTemplateVersionStepUi,
  isCombinedStepConfig,
  isUiStepConfig,
} from 'dashboard/models/inquiry-template-version';
import {
  WorkflowStep,
  WorkflowStepConditional,
  WorkflowStepType,
  WorkflowVersion,
} from 'dashboard/models/workflow-version';
import { FieldSchema } from 'lib/fields';
import { isUiStep, StepPreview, StepUi, UiStepType } from 'lib/interfaces/StepsConfig';
import {
  isUiParentComponentConfig,
  isUiTransitionComponentConfig,
  UiComponentConfig,
  UiComponentConfigType,
} from 'lib/interfaces/ui-components';
import { captureMessage } from 'lib/reporting';
import { isNumeric } from 'lib/utils/formValidation';
import { assertNever } from 'lib/utils/types';

export const defaultNewStepName = 'ui_step';
export const defaultNewStepNameDisplay = 'New Step';

/**
 * Prepares copied screen tree step config to be stored in clipboard
 * by clearing statuses and transition configs to ensure a clean slate
 * for whatever location the step is pasted in.
 * @param copiedTreeStep screen tree step config
 * @returns new step config + step preview with removed statuses and cleared transition configs
 */
export const copiedStepSanitized = (copiedTreeStep: TemplateStepPreview): NewStep => {
  // extract ITV step-only attributes, then clear statuses and existing transition configs
  const { combinedStepPages, page, stepPreview, ...templateStep } = copiedTreeStep;
  const sanitizedStepConfig = stepWithClearedTransitions(
    stepWithUpdatedStatuses({
      step: templateStep as ScreenStepConfig,
      statuses: [],
    }),
  );
  // conditionally update the step component preview based on terminal conditions
  const sanitizedStepPreview = isUiStep(stepPreview)
    ? uiStepPreviewWithClearedTransitions(stepPreview)
    : stepPreview;
  return {
    stepConfig: sanitizedStepConfig,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    stepPreview: sanitizedStepPreview,
  };
};

/**
 * Centralized logic for updating an existing step config/preview to be inserted into the flow tree.
 * May update name/name-display, transition config, and statuses, based on insertion location
 * Used in cut/copy + paste and module insertions.
 * @param newStep existing step config/preview to insert
 * @param flowTreeData steps, stepsPreview, workflowVersionMap
 * @param addStepConfig
 * @param omitUpdateConfig
 * @returns new step config/preview with updated transition and statuses based on insert location
 */
export const newStepUpdatedToInsert = ({
  newStep,
  flowTreeData,
  addStepConfig,
  omitUpdateConfig,
}: {
  newStep: NewStep;
  flowTreeData: FlowTreeData;
  addStepConfig: AddStepConfig;
  omitUpdateConfig?: OmitUpdateConfig;
}): NewStep => {
  const { stepConfig, stepPreview } = newStep;
  const { prevStep, nextStep } = addStepConfig;
  let updatedStepConfig = stepConfig;
  let updatedStepPreview = stepPreview;

  if (newStep.presentInTree) {
    const { name, nameDisplay } = newStepName(flowTreeData.steps, {
      name: stepConfig.name,
      nameDisplay: getDisplayNameForStep(stepConfig),
    });
    updatedStepConfig = stepWithUpdatedNames({
      step: stepConfig,
      name,
      nameDisplay,
    });
    updatedStepPreview = set(['name'], name, stepPreview);
  }

  // the omit config needs to be the outer condition as it's used for modules with multiple steps/workflows.
  // in that scenario, the initial module step should not be updated the same as a single step.
  // - for a terminal insert location, the initial step is not the terminal step due to the remainder of the module
  // - for a non-terminal insert location, the initial step is already configured to transition to the remainder of the module
  if (!omitUpdateConfig?.omitSelfNext) {
    if (nextStep == null) {
      if (isUiStepConfig(updatedStepConfig) && isUiStep(updatedStepPreview)) {
        updatedStepConfig = uiStepTerminalUpdated(updatedStepConfig);
        updatedStepPreview = uiStepPreviewTerminalUpdated(updatedStepPreview);
      }
      if (isScreenStep(prevStep) && isTerminalStep(prevStep)) {
        updatedStepConfig = stepWithUpdatedStatuses({
          step: updatedStepConfig,
          statuses: [
            ...prevStep.config.statuses.filter(({ terminal }) => terminal),
            ...updatedStepConfig.config.statuses,
          ],
        });
      }
    } else {
      const { type, token } = nextStep;
      updatedStepConfig = stepTransitionUpdated({
        step: updatedStepConfig,
        transitionType: type === 'workflow' ? 'workflow' : 'step',
        targetToken: type === 'workflow' ? nextStep.workflowVersionId : token,
        workflowErrorStepName: updatedStepConfig.name,
      });
    }
  }

  return {
    stepConfig: updatedStepConfig,
    stepPreview: updatedStepPreview,
  };
};

/**
 * Creates new default blank screen step config/preview based on
 * insertion location and conditions. Used for the 'screen'
 * option in the flow tree add step button dropdown.
 * @param addStepConfig
 * @param steps
 * @returns new step config and step preview
 */
const newBlankStepToInsert = (
  addStepConfig: AddStepConfig,
  steps: InquiryTemplateVersionStep[],
): NewStep => {
  const { prevStep, nextStep } = addStepConfig;
  const terminal = nextStep == null;
  const statuses =
    terminal && isScreenStep(prevStep) && isTerminalStep(prevStep)
      ? prevStep.config.statuses.filter(({ terminal }) => terminal)
      : null;
  const stepConfig = createBlankUiStep(newStepName(steps), {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    toStep: nextStep?.type === 'workflow' ? null : nextStep?.token,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    workflowVersionId: nextStep?.type === 'workflow' ? nextStep.workflowVersionId : null,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    statuses,
    terminal,
  });
  const stepPreview = createBlankUiStepPreview(stepConfig.name, { terminal });
  return {
    stepConfig,
    stepPreview,
  };
};

/**
 * Keeps workflowVersionMap and vtmplVersionMap in ITE
 * updated with only objects referenced in the latest
 * tree data (steps, workflows) in redux-form
 *
 * @param workflowVersionMap map returned after last ITV save
 * @param vtmplVersionMap map returned after last ITV save
 * @param steps updated steps data
 * @returns updated maps containing only connected workflows and vtmplvs
 */
export const getConnectedSchemaMaps = (
  workflowVersionMap: WorkflowVersionMap,
  vtmplVersionMap: VerificationTemplateVersionMap,
  steps: InquiryTemplateVersionStep[],
): {
  connectedWorkflowVersionMap: WorkflowVersionMap;
  connectedVtmplvMap: VerificationTemplateVersionMap;
} => {
  const connectedWorkflowVersionIds = stepsWorkflowVersionTargets(steps);
  const connectedWorkflowVersionMap = pickBy(
    ({ id }) => connectedWorkflowVersionIds.includes(id),
    workflowVersionMap,
  );
  // dups should be fine here, since we're only checking for inclusion
  const connectedVtmplvIds = [
    ...stepsVtmplvIds(steps),
    ...Object.values(connectedWorkflowVersionMap).flatMap((workflowVersion: WorkflowVersion) =>
      workflowVtmplvIds(workflowVersion.attributes['steps-config']),
    ),
  ];
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const connectedVtmplvMap = pickBy(({ id }) => connectedVtmplvIds.includes(id), vtmplVersionMap);
  return {
    connectedWorkflowVersionMap,
    connectedVtmplvMap,
  };
};

/**
 * Helper for setting correct root active node token when
 * deep linking or panning to a conditional branch node.
 * @param nodeToken token for step or conditional branch node in InquiryTemplateTree
 * @returns token with branch index subpath removed, if present
 */
export const getRootNodeToken = (nodeToken: string): string => {
  const splitToken = nodeToken.split('.');
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return isNumeric(splitToken.pop()) ? splitToken.join('.') : nodeToken;
};

/**
 * Helper for getting flow editor node wrapper data ids and labels
 * @returns data-marketing-id, data-name, data-test-id, title
 */
export const getNodeWrapperAttributes = (step: CombinedStepForTree): NodeWrapperAttributes => {
  const token = step.type === 'workflow' ? step.workflowStep.token : step.token;
  const name = step.type === 'workflow' ? step.workflowStep.name : step.name;
  const type = step.type === 'workflow' ? step.workflowStep.type : step.type;
  const dataMarketingId = `ite-tree-card__${type}`;
  const dataName = kebabCase(name);
  const dataTest = `${token}__${type}-node`;
  const title = step.type === 'workflow' ? name : getDisplayNameForStep(step);
  return { dataMarketingId, dataName, dataTest, title };
};

export const isTerminalStep = (step: InquiryTemplateVersionStep | undefined): boolean => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return step?.config?.statuses?.some(({ terminal }) => terminal);
};

// Used in InquiryTemplateTree to determine how to wire up the
// current step's transition config to the next step.
export const isNonWorkflowNextStep = (
  step: CombinedStepForTree | InquiryTemplateVersionStep | null,
): step is ScreenStepConfig | InquiryTemplateVersionStepComplete => {
  switch (step?.type) {
    case UiStepType.Complete:
      return true;
    default:
      return isScreenStep(step);
  }
};

export const isScreenStep = (
  step: CombinedStepForTree | InquiryTemplateVersionStep | null,
): step is ScreenStepConfig => {
  switch (step?.type) {
    case UiStepType.Document:
    case UiStepType.GovernmentId:
    case UiStepType.Integration:
    case UiStepType.VerifyWithPersona:
    case UiStepType.Selfie:
    case UiStepType.Ui:
    case UiStepType.Loop:
      return true;
    default:
      return false;
  }
};

/**
 * Extracts vtmplv attributes from module fetch payload and
 * updates redux-form vtmplVersionMap and vtmplv(s).
 *
 * Vtmplv names are not validated for uniqueness on the server, so the util also
 * generates a reasonable "unique" name starting point to help users distinguish between
 * vtmplv configs by incrementing from the vtmplv type count of the currently inserted vtmplv(s)
 *
 * @returns updated vtmplVersionMap
 */
export const vtmplVersionMapWithInserted = (
  moduleVtmplvs: ModuleVerificationTemplateVersion[],
  vtmplVersionMap: VerificationTemplateVersionMap,
): VerificationTemplateVersionMap => {
  return moduleVtmplvs.reduce((vtmplVersionMap, { id, name, type, config }) => {
    const typeCount = Object.values(vtmplVersionMap).filter(
      ({ type: vtmplvType }) => vtmplvType === type,
    ).length;
    return {
      ...vtmplVersionMap,
      [id]: createMockVerificationTemplateVersion(id, type, `${name} ${typeCount + 1}`, config),
    };
  }, vtmplVersionMap);
};

/**
 * Helper for stepsWorkflowsWithInsertedModule()
 * Updates workflowVersionMap with updated/new workflow versions.
 *
 * First filters falsy because flow tree insert helpers optionally
 * return updated workflow versions, depending on the inserted steps type
 * and the insertion position
 *
 * @returns updated workflow version map
 */
export const getWorkflowVersionMapUpdated = (
  workflowVersionMap: WorkflowVersionMap,
  updatedWorkflowVersions: (WorkflowVersion | null | undefined)[],
): WorkflowVersionMap => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return updatedWorkflowVersions.filter(Boolean).reduce(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    (workflowVersionMap, workflowVersion) => ({
      ...workflowVersionMap,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      [workflowVersion.id]: workflowVersion,
    }),
    workflowVersionMap,
  );
};

/**
 * Ensures new steps always have a unique name.
 *
 * @returns step_name and name_display
 */
export const newStepName = (
  steps: InquiryTemplateVersionStep[],
  newStepName?: { name: string; nameDisplay: string },
): { name: string; nameDisplay: string } => {
  const stepName = newStepName?.name == null ? defaultNewStepName : newStepName.name;
  const stepNameDisplay =
    newStepName?.nameDisplay == null ? defaultNewStepNameDisplay : newStepName.nameDisplay;
  // find the largest numbered step named stepName and create that step + 1
  // if no such step exists, create step named stepName
  // this method is more robust to handle deleted steps in the middle of the sequence
  const newNumberedStepRegex = new RegExp(`^${stepName}_([0-9]*)$`);
  const maxStepNumber = steps.reduce((maxStepNumber, step) => {
    // step 1 doesn't have an enumeration in step name
    const stepNumber = step.name === stepName ? '1' : newNumberedStepRegex.exec(step.name)?.[1];
    if (stepNumber && parseInt(stepNumber) > maxStepNumber) {
      return parseInt(stepNumber);
    } else {
      return maxStepNumber;
    }
  }, 0);

  // don't append _1 when creating first new step
  if (maxStepNumber === 0) {
    return {
      name: stepName,
      nameDisplay: stepNameDisplay,
    };
  }

  return {
    name: `${stepName}_${maxStepNumber + 1}`,
    nameDisplay: `${stepNameDisplay} ${maxStepNumber + 1}`,
  };
};

/**
 * Handles adding new fields introduced by an inserted module.
 * Only net-new fields to redux-store, we reuse/override existing fields by default
 *
 * @returns updated field_schema to be dispatched to redux store
 */
export const getDedupedFieldSchema = (
  templateFields: FieldSchema[],
  moduleFields: ModuleFieldSchema[],
): FieldSchema[] => {
  const newFields = moduleFields
    .filter(
      (moduleField) =>
        !templateFields.find((templateField) => templateField.key === moduleField.key),
    )
    .map(({ description, ...fieldSchema }) => fieldSchema);
  return templateFields.concat(newFields);
};

const createConditionalStepWithStepsInFirstBranch = (
  steps: WorkflowStep[],
): WorkflowStepConditional => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return createMockConditionalStep(null, [steps, []]);
};

/**
 * @returns workflow step token corresponding to the given step + branch index (if step is conditional)
 */
export const getTargetToken = ({
  step,
  workflowVersionMap,
  branchIndex,
}: {
  step: LinkedWorkflowStep;
  workflowVersionMap: WorkflowVersionMap;
  branchIndex?: number;
}) => {
  if (step.workflowStep.type !== WorkflowStepType.Conditional || branchIndex == null) {
    return step.workflowStep.token;
  } else {
    const workflowStep = getConditionalStepForToken(
      step.workflowStep.token,
      workflowVersionMap[step.workflowVersionId],
    );
    return workflowStep?.branches?.[branchIndex]?.[0]?.token;
  }
};

export const getConditionalStepForToken = (
  token: string,
  workflowVersion: WorkflowVersion | undefined,
): WorkflowStepConditional | undefined =>
  getAllWorkflowConditionalSteps(workflowVersion?.attributes?.['steps-config'] ?? []).find(
    (step) => step.token === token,
  );

export const getTransitionComponentName = (
  step: CombinedStepForTree,
  branchIndex = 0,
): string | undefined => {
  if (isScreenStep(step) && step.type === UiStepType.Ui) {
    const transitionComponent = uiStepAllComponentConfigs(step).filter(
      isUiTransitionComponentConfig,
    )[branchIndex];

    return transitionComponent?.name;
  }
};

/**
 * Helper for stepsWorkflowsWithInsertedModule()
 *
 * Extracts incoming screen config/preview, non initial/final
 * screens and wfvs, and optional outgoing connection, as well as
 * flags for the new module's terminal and single-object statuses
 *
 * @returns NewModuleConfig
 */
export const getNewModuleConfig = (
  moduleAttributes: FlowTreeModuleAttributes,
  addStepConfig: AddStepConfig,
): NewModuleConfig | undefined | null => {
  const {
    'incoming-connection': { id: incomingId },
    'outgoing-connection': moduleOutgoing,
    'workflow-versions': workflowVersions,
    screens: moduleScreens,
  } = moduleAttributes;

  const isTerminalModule = !moduleOutgoing;
  const sharedIncomingOutgoingConnection = incomingId === moduleOutgoing?.id;
  const isSingleObject =
    sharedIncomingOutgoingConnection && moduleScreens.length + workflowVersions.length === 1;
  const initialScreen = moduleScreens.find((moduleScreen) => incomingId === moduleScreen.name);
  if (!initialScreen) {
    captureMessage(
      `Inserting module with incoming connection id: ${incomingId}, but object was not found`,
      'warning',
    );
    return null;
  }

  const [moduleSteps, moduleStepPreviews] = getOtherModuleStepsAndPreviews(
    moduleScreens,
    incomingId,
    moduleOutgoing?.id,
  );

  const moduleWorkflowVersions = workflowVersions
    .filter(({ id }) => ![incomingId, moduleOutgoing?.id].includes(id))
    .map(({ id, description, 'steps-config': stepsConfig }) =>
      createMockWorkflowVersionV2(
        {
          description,
          'steps-config': stepsConfig,
        },
        {},
        id,
      ),
    );

  let outgoingObject = null;
  if (!isTerminalModule && !isSingleObject) {
    switch (moduleOutgoing.type) {
      case ModuleConnectionType.Screen: {
        outgoingObject = moduleScreens.find(({ name }) => name === moduleOutgoing?.id);
        outgoingObject = outgoingObject
          ? { stepConfig: outgoingObject['as-json'], stepPreview: outgoingObject['client-data'] }
          : null;
        break;
      }
      case ModuleConnectionType.WorkflowVersion: {
        outgoingObject = workflowVersions.find(({ id }) => id === moduleOutgoing?.id);
        // we currently assume that any module outgoing-wfvs must contain a conditional step
        if (
          outgoingObject &&
          !outgoingObject['steps-config'].find(({ type }) => type === WorkflowStepType.Conditional)
        ) {
          captureMessage(
            `Inserted module's outgoing connection wfv: ${moduleOutgoing.id} is expected to contain a conditional step`,
            'warning',
          );
          return null;
        }
        break;
      }
      default:
        assertNever(moduleOutgoing.type);
    }

    if (!outgoingObject) {
      captureMessage(
        `Inserting module with outgoing connection id: ${moduleOutgoing.id}, but object was not found`,
        'warning',
      );
      return null;
    }
  }

  const incomingConnection = {
    stepConfig: initialScreen['as-json'],
    stepPreview: initialScreen['client-data'],
  };

  // Added screen inherits status from previous step or defaults to complete, if in terminal position
  if (addStepConfig.nextStep == null && moduleScreens.length === 1 && !isTerminalModule) {
    incomingConnection.stepConfig.config.statuses =
      isScreenStep(addStepConfig.prevStep) && isTerminalStep(addStepConfig.prevStep)
        ? addStepConfig.prevStep.config.statuses.filter(({ terminal }) => terminal)
        : [{ name: 'completed', terminal: true, initial: false }];
  }

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const outgoingConnection: NewModuleOutgoing | undefined = isTerminalModule
    ? null
    : {
        type: moduleOutgoing.type,
        object: isSingleObject ? incomingConnection : outgoingObject,
      };

  return {
    incomingConnection,
    moduleSteps,
    moduleStepPreviews,
    moduleWorkflowVersions,
    outgoingConnection,
    sharedIncomingOutgoingConnection,
    isTerminalModule,
    isSingleObject,
  };
};

/**
 * Updates flow editor tree data - stepsConfig, stepsPreview,
 * and workflowVersionMap when inserting a new module
 *
 * Current assumptions made to reduce scope
 * 1. Incoming connection is always of type 'screen'
 *    - allows us to omit type checking and initial wfv handling
 *      logic until there are available modules that are
 *      wfv-initial or wfv-only
 * 2. Outgoing connection wfvs always contain a conditional step
 *    - there isn't currently an obvious module use case with an outgoing
 *      wfv that only contains action steps
 *    - similar to inserting a new branch node, the steps from nextStep at
 *      the insertion position on are added to the 0-index branch of the trailing wfv
 *
 * @returns updated tree data to be dispatched to redux store
 */
export const stepsWorkflowsWithInsertedModule = (
  flowTreeData: FlowTreeData,
  newModuleConfig: NewModuleConfig | undefined | null,
  addStepConfig: AddStepConfig,
): UpdatedStepsPreviewsWorkflowMap => {
  if (!newModuleConfig) {
    return {};
  }

  const {
    incomingConnection,
    moduleSteps,
    moduleStepPreviews,
    moduleWorkflowVersions,
    outgoingConnection,
    isTerminalModule,
    isSingleObject,
    sharedIncomingOutgoingConnection,
  } = newModuleConfig;

  // first wire up connections from existing nodes to initial module step
  const { updatedSteps, updatedStepsPreview, updatedWorkflowVersion } =
    stepsWorkflowsPreviewsWithInserted({
      flowTreeData,
      addStepConfig,
      newStep: incomingConnection,
      omitUpdateConfig: {
        omitNeighborBack: !isSingleObject,
        omitSelfNext: !isSingleObject,
      },
    });

  const updatedWorkflowMap = getWorkflowVersionMapUpdated(flowTreeData.workflowVersionMap, [
    updatedWorkflowVersion,
  ]);

  if (isSingleObject) {
    return {
      updatedSteps,
      updatedStepsPreview,
      updatedWorkflowMap,
    };
  }

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const stepsWithAdditional = updatedSteps.concat(moduleSteps);
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const previewsWithAdditional = updatedStepsPreview.concat(moduleStepPreviews);
  const workflowMapWithAdditional = getWorkflowVersionMapUpdated(
    updatedWorkflowMap,
    moduleWorkflowVersions,
  );

  if (isTerminalModule) {
    return {
      updatedSteps: stepsWithAdditional,
      updatedStepsPreview: previewsWithAdditional,
      updatedWorkflowMap: workflowMapWithAdditional,
    };
  }

  // update module outgoing connection's transition target and back_step (if present), then return
  return stepsWorkflowsWithModuleFinal(
    {
      steps: stepsWithAdditional,
      stepsPreview: previewsWithAdditional,
      workflowVersionMap: workflowMapWithAdditional,
    },
    addStepConfig,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    outgoingConnection,
    sharedIncomingOutgoingConnection,
  );
};

/**
 * Helper for stepsWorkflowsWithInsertedModule().
 * Extracts step config and preview data from module response for non initial/final screen steps,
 * which can be directly inserted into the redux-form data arrays since any transition connections
 * are already correctly wired within the new module
 *
 * @returns list of step configs and previews
 */
const getOtherModuleStepsAndPreviews = (
  moduleScreens: ModuleScreen[],
  incomingId: string,
  outgoingId: string | undefined,
): [ScreenStepConfig[], StepPreview[]] => {
  const otherModuleScreens = moduleScreens.filter(
    (moduleScreen) => ![incomingId, outgoingId].includes(moduleScreen.name),
  );
  const moduleSteps = otherModuleScreens.map(({ 'as-json': stepConfig }) => stepConfig);
  const moduleStepPreviews = otherModuleScreens.map(({ 'client-data': clientData }) => clientData);

  return [moduleSteps, moduleStepPreviews];
};

/**
 * Helper for stepsWorkflowsWithModuleFinal()
 * Has two main responsibilities:
 * 1. Filters out all updateInquiryNextStep actions with null next-step-name param from module wfv
 *    - easier to handle because we automatically insert a new one if
 *      none are found while wiring up flow tree connections on the client.
 *    - connections that are already wired up within the module itself will
 *      be preserved (e.g retry branch)
 * 2. Appends the provided connectingSteps to the terminal 0-index branch
 *    - when nextStep is a workflow node, connectingSteps is all of that wfv's steps,
 *      as the two wfvs are merged (module wfv's steps are prepended)
 *    - when nextStep is a screen node, connectingSteps contains a single updateInquiryNextStep action
 *    - when nextStep is null, connectingSteps is empty
 *
 * @returns updated workflow steps
 */
export const getUpdatedModuleWorkflowSteps = (
  steps: WorkflowStep[],
  connectingSteps: WorkflowStep[],
): WorkflowStep[] => {
  return steps
    .filter((step) =>
      isUpdateInquiryNextStepActionStep(step) ? step.action.params['next-step-name'] : true,
    )
    .map((step) =>
      step.type === WorkflowStepType.Conditional
        ? {
            ...step,
            branches: step.branches.map((branch, i) => {
              const stepsToPass = i === 0 ? connectingSteps : [];
              return i === 0 &&
                !branch.find((branchStep) => branchStep.type === WorkflowStepType.Conditional)
                ? getUpdatedModuleWorkflowSteps(branch, stepsToPass).concat(connectingSteps)
                : getUpdatedModuleWorkflowSteps(branch, stepsToPass);
            }),
          }
        : step,
    );
};

/**
 * Helper for stepsWorkflowsWithModuleFinal()
 * Used to get prevStep from within the new module
 * screens if the module contains a wfv that's being added
 * to an existing wfv in the tree (as the id will now need to match)
 *
 * @returns step containing the wfv token as a transition target
 */
export const getStepForWfvTarget = (
  steps: InquiryTemplateVersionStep[],
  wfvToken: string,
): InquiryTemplateVersionStep | undefined => {
  return steps.find((step) => {
    if (isCombinedStepConfig(step)) {
      return step.config['workflow-version-id'] === wfvToken;
    } else if (isUiStepConfig(step)) {
      return uiStepAllComponentConfigs(step)
        .filter(isUiTransitionComponentConfig)
        .find(({ config }) => config['workflow-version-id'] === wfvToken);
    } else {
      return false;
    }
  });
};

/**
 * Helper for stepsWorkflowsWithInsertedModule().
 * Handles setting the correct transition target, and updating flow tree data
 * with the new step and step preview.
 *
 * omitNeighborNext for an outgoing screen connection ensures only the back_step
 * config is updated, as the prevStep transition target is handled either in
 * stepsWorkflowsWithModuleInitial(), or within the module itself.
 *
 * If the outgoing connection and nextStep are both wfvs, the module wfv's steps
 * are prepended to the nextStep's wfv in order to update the existing wfv rather than
 * discarding it and creating a new one. Finally, the module step that transitions to
 * the outgoing module wfv has its workflow-version-id target updated.
 *
 * @returns updated steps, stepsPreview, workflowVersionMap
 */
export const stepsWorkflowsWithModuleFinal = (
  flowTreeData: FlowTreeData,
  addStepConfig: AddStepConfig,
  outgoingConnection: NewModuleOutgoing,
  sharedIncomingOutgoingConnection: boolean,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
): UpdatedStepsPreviewsWorkflowMap => {
  const { nextStep } = addStepConfig;
  const { steps, stepsPreview, workflowVersionMap } = flowTreeData;
  const { type, object } = outgoingConnection;
  if (type === ModuleConnectionType.Screen) {
    const outgoingScreen = object as NewStep;
    // If the outgoing screen is the same as the incoming screen, we just need
    // to update the screens transition config to point to the next step if non-null
    if (sharedIncomingOutgoingConnection) {
      let updatedSteps = steps;
      if (nextStep != null) {
        updatedSteps = steps.map((step) => {
          return step.name === outgoingScreen.stepConfig.name && isUiStepConfig(step)
            ? stepTransitionUpdated({
                step: step,
                transitionType: nextStep?.type === 'workflow' ? 'workflow' : 'step',
                targetToken: nextStep?.type === 'workflow' ? nextStep.token : nextStep.name,
                workflowErrorStepName: step.name,
              })
            : step;
        });
      }
      return {
        updatedSteps,
        updatedStepsPreview: stepsPreview,
        updatedWorkflowMap: workflowVersionMap,
      };
    } else {
      const { updatedSteps, updatedStepsPreview, updatedWorkflowVersion } =
        stepsWorkflowsPreviewsWithInserted({
          flowTreeData,
          addStepConfig,
          newStep: outgoingScreen,
          omitUpdateConfig: {
            omitNeighborNext: true,
          },
        });
      return {
        updatedSteps,
        updatedStepsPreview,
        updatedWorkflowMap: getWorkflowVersionMapUpdated(workflowVersionMap, [
          updatedWorkflowVersion,
        ]),
      };
    }
  } else if (type === ModuleConnectionType.WorkflowVersion) {
    const outgoingModuleWfv = object as ModuleWorkflowVersion;
    const updatedWorkflowVersion =
      nextStep?.type === 'workflow'
        ? set(
            ['attributes', 'steps-config'],
            getUpdatedModuleWorkflowSteps(
              outgoingModuleWfv['steps-config'],
              workflowVersionMap[nextStep.workflowVersionId].attributes['steps-config'],
            ),
            workflowVersionMap[nextStep.workflowVersionId],
          )
        : createMockWorkflowVersionV2(
            {
              description: outgoingModuleWfv.description,
              'steps-config': getUpdatedModuleWorkflowSteps(
                outgoingModuleWfv['steps-config'],
                nextStep
                  ? [createMockActionStep('route', (nextStep as InquiryTemplateVersionStep).name)]
                  : [],
              ),
            },
            {},
            outgoingModuleWfv.id,
          );
    const prevStep = getStepForWfvTarget(steps, outgoingModuleWfv.id);
    const updatedSteps =
      nextStep?.type === 'workflow'
        ? stepsConfigTransitionsUpdated(steps, 'workflow', updatedWorkflowVersion.id, {
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            prevStep: {
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              token: prevStep.name,
              ...prevStep,
            },
            nextStep: null,
          })
        : steps;
    return {
      updatedSteps,
      updatedStepsPreview: stepsPreview,
      updatedWorkflowMap: getWorkflowVersionMapUpdated(workflowVersionMap, [
        updatedWorkflowVersion,
      ]),
    };
  }
};

/**
 * Function used to update the 'tree' data used in flow editor - stepsConfig and workflowVersionMap.
 * when adding a new action node.
 *
 * @returns updated tree data to be dispatched to redux store
 */
export const stepsWorkflowsWithInsertedActionNode = (
  flowTreeData: FlowTreeData,
  addStepConfig: AddStepConfig,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
): UpdatedStepsPreviewsWorkflows => {
  const { steps, stepsPreview, workflowVersionMap } = flowTreeData;
  const { branchIndex, nextStep, prevStep } = addStepConfig;
  const newActionStep: WorkflowStep = createMockActionStep('placeholder');

  if (isScreenStep(prevStep) && (nextStep == null || isNonWorkflowNextStep(nextStep))) {
    const componentName = getTransitionComponentName(prevStep, branchIndex);
    const newWorkflowVersion = createMockWorkflowVersionV2(
      {
        description: 'Placeholder Action',
        'steps-config': nextStep
          ? [
              newActionStep,
              createMockActionStep('route', (nextStep as InquiryTemplateVersionStep).name),
            ]
          : [newActionStep],
      },
      {},
      `placeholder_${uuid()}`,
    );

    const { updatedSteps, updatedStepsPreview } = stepsConfigPreviewsUpdated(
      steps,
      stepsPreview,
      'workflow',
      newWorkflowVersion.id,
      addStepConfig,
      null,
      componentName,
    );

    return {
      updatedSteps,
      updatedStepsPreview: stepsPreview === updatedStepsPreview ? null : updatedStepsPreview,
      updatedWorkflowVersion: newWorkflowVersion,
    };
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  } else if (isScreenStep(prevStep) && nextStep.type === 'workflow') {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const workflowVersion = workflowVersionMap[nextStep.workflowVersionId];
    return {
      // don't need to update itv step config here, because the step before is still going to the same
      // workflow version, just with an extra workflow step in it
      updatedWorkflowVersion: {
        ...workflowVersion,
        attributes: {
          ...workflowVersion.attributes,
          'steps-config': [newActionStep, ...workflowVersion.attributes['steps-config']],
        },
      },
    };
  } else if (prevStep.type === 'workflow') {
    const workflowVersion = workflowVersionMap[prevStep.workflowVersionId];

    return {
      // don't need to update itv step config here, because the step before is still using the same workflow
      // with the conditional step, just with an extra workflow step tacked onto that branch
      updatedWorkflowVersion: {
        ...workflowVersion,
        attributes: {
          ...workflowVersion.attributes,
          'steps-config': insertWorkflowStepAfterTokenAndBranch(
            workflowVersion.attributes['steps-config'],
            prevStep.workflowStep.token,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            branchIndex,
            newActionStep,
          ),
        },
      },
    };
  }
};

/**
 * Function used to update the 'tree' data used in flow editor - stepsConfig and workflowVersionMap.
 * when adding a new branching node.
 *
 * @returns updated tree data to be dispatched to redux store
 */
export const stepsWorkflowsWithInsertedBranchingNode = (
  flowTreeData: FlowTreeData,
  addStepConfig: AddStepConfig,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
): UpdatedStepsPreviewsWorkflows => {
  const { steps, stepsPreview, workflowVersionMap } = flowTreeData;
  const { branchIndex, nextStep, prevStep } = addStepConfig;

  if (isScreenStep(prevStep) && (nextStep == null || isNonWorkflowNextStep(nextStep))) {
    const componentName = getTransitionComponentName(prevStep, branchIndex);
    const newWorkflowVersion = createMockWorkflowVersionV2(
      {
        description: 'New Branching Step',
        'steps-config': [
          createConditionalStepWithStepsInFirstBranch(
            nextStep
              ? [createMockActionStep('route', (nextStep as InquiryTemplateVersionStep).name)]
              : [],
          ),
        ],
      },
      {},
      `placeholder_${uuid()}`,
    );

    const { updatedSteps, updatedStepsPreview } = stepsConfigPreviewsUpdated(
      steps,
      stepsPreview,
      'workflow',
      newWorkflowVersion.id,
      addStepConfig,
      null,
      componentName,
    );

    return {
      updatedSteps,
      updatedStepsPreview: stepsPreview === updatedStepsPreview ? null : updatedStepsPreview,
      updatedWorkflowVersion: newWorkflowVersion,
    };
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  } else if (isScreenStep(prevStep) && nextStep.type === 'workflow') {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const workflowVersion = workflowVersionMap[nextStep.workflowVersionId];
    return {
      // don't need to update itv step config here, because the step before is still going to the same
      // workflow version, just with an extra workflow step in it
      updatedWorkflowVersion: {
        ...workflowVersion,
        attributes: {
          ...workflowVersion.attributes,
          'steps-config': [
            createConditionalStepWithStepsInFirstBranch(workflowVersion.attributes['steps-config']),
          ],
        },
      },
    };
  } else if (prevStep.type === 'workflow') {
    const workflowVersion = workflowVersionMap[prevStep.workflowVersionId];

    const targetToken = getTargetToken({ step: prevStep, workflowVersionMap, branchIndex });

    if (nextStep == null || nextStep.type === 'workflow') {
      return {
        // don't need to update itv step config here, because the step before is still using the same workflow
        // with the conditional step, just with an extra workflow step tacked onto that branch
        updatedWorkflowVersion: {
          ...workflowVersion,
          attributes: {
            ...workflowVersion.attributes,
            'steps-config': createBranchAtToken(
              workflowVersion.attributes['steps-config'],
              prevStep.workflowStep.token,
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              branchIndex,
            ),
          },
        },
      };
    } else if (isNonWorkflowNextStep(nextStep)) {
      return {
        // don't need to update itv step config here, because the step before is still using the same workflow
        // with the conditional step, just with an extra workflow step tacked onto that branch
        updatedWorkflowVersion: {
          ...workflowVersion,
          attributes: {
            ...workflowVersion.attributes,
            'steps-config': createBranchBeforeStepTransition(
              workflowVersion.attributes['steps-config'],
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              targetToken,
            ),
          },
        },
      };
    }
  }
};

/**
 * Function used to update the 'tree' data used in flow editor - stepsConfig and workflowVersionMap.
 * when adding a new connector node.
 *
 * @returns updated tree data to be dispatched to redux store
 */
export const stepsWorkflowsWithInsertedConnectorNode = (
  flowTreeData: FlowTreeData,
  addStepConfig: AddStepConfig,
): UpdatedStepsPreviewsWorkflows => {
  const { steps, stepsPreview, workflowVersionMap } = flowTreeData;
  const { prevStep, nextStep, branchIndex, connectingStepName } = addStepConfig;

  if (isScreenStep(prevStep)) {
    const componentName = getTransitionComponentName(prevStep, branchIndex);
    const { updatedSteps, updatedStepsPreview } = stepsConfigPreviewsUpdated(
      steps,
      stepsPreview,
      'step',
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      connectingStepName,
      {
        ...addStepConfig,
        nextStep: null, // don't pass next step to not modify back behavior on next step which will be removed from the tree
      },
      null,
      componentName,
    );

    return {
      updatedSteps,
      updatedStepsPreview,
      updatedWorkflowVersion: null,
    };
  } else if (
    prevStep.type === 'workflow' &&
    (nextStep == null || isNonWorkflowNextStep(nextStep))
  ) {
    const workflowVersion = workflowVersionMap[prevStep.workflowVersionId];
    const updatedWorkflowVersion = set(
      ['attributes', 'steps-config'],
      workflowStepsUpdated(
        workflowVersion.attributes['steps-config'],
        prevStep.workflowStep.token,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        branchIndex,
        connectingStepName,
      ),
      workflowVersion,
    );

    const { updatedSteps, updatedStepsPreview } = stepsConfigPreviewsUpdated(
      steps,
      stepsPreview,
      'step',
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      connectingStepName,
      {
        ...addStepConfig,
        nextStep: null, // don't pass next step to not modify back behavior on next step which will be removed from the tree
      },
    );

    return {
      updatedSteps,
      updatedStepsPreview,
      updatedWorkflowVersion,
    };
  } else {
    return { updatedSteps: null, updatedStepsPreview: null, updatedWorkflowVersion: null };
  }
};

/**
 * Parent function responsible for routing to the correct helpers
 * used to update the 'tree' data used in flow editor - stepsConfig, stepsPreview, and workflowVersionMap.
 *
 * The main helpers are:
 * stepsPreviewsWithInserted(): handles inserting the new step
 * stepsConfigTransitionsUpdated(): handles updating the connections between steps
 * workflowStepsUpdated(): handles updating the next_step_name param
 *
 * @returns updated tree data to be dispatched to redux store
 */
export const stepsWorkflowsPreviewsWithInserted = ({
  flowTreeData,
  addStepConfig,
  newStep,
  omitUpdateConfig,
}: {
  flowTreeData: FlowTreeData;
  addStepConfig: AddStepConfig;
  newStep?: NewStep;
  omitUpdateConfig?: OmitUpdateConfig;
}): UpdatedStepsPreviewsWorkflows => {
  const { stepConfig, stepPreview } =
    newStep != null
      ? newStepUpdatedToInsert({
          newStep,
          flowTreeData,
          addStepConfig,
          omitUpdateConfig,
        })
      : newBlankStepToInsert(addStepConfig, flowTreeData.steps);

  const { prevStep, nextStep, branchIndex } = addStepConfig;
  const { steps, stepsPreview } = flowTreeData;
  let updatedSteps = steps.concat(stepConfig);
  let updatedStepsPreview = stepsPreview.concat(stepPreview);

  if (isScreenStep(prevStep)) {
    const componentName = getTransitionComponentName(prevStep, branchIndex);
    ({ updatedSteps, updatedStepsPreview } = stepsConfigPreviewsUpdated(
      updatedSteps,
      updatedStepsPreview,
      'step',
      stepConfig.name,
      addStepConfig,
      omitUpdateConfig,
      componentName,
    ));

    return {
      updatedSteps,
      updatedStepsPreview,
      updatedWorkflowVersion: null,
    };
  } else if (
    prevStep.type === 'workflow' &&
    (nextStep == null || isNonWorkflowNextStep(nextStep))
  ) {
    const workflowVersion = flowTreeData.workflowVersionMap[prevStep.workflowVersionId];
    const updatedWorkflowVersion = set(
      ['attributes', 'steps-config'],
      workflowStepsUpdated(
        workflowVersion.attributes['steps-config'],
        prevStep.workflowStep.token,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        branchIndex,
        stepConfig.name,
      ),
      workflowVersion,
    );

    ({ updatedSteps, updatedStepsPreview } = stepsConfigPreviewsUpdated(
      updatedSteps,
      updatedStepsPreview,
      'step',
      stepConfig.name,
      addStepConfig,
      omitUpdateConfig,
    ));

    return {
      updatedSteps,
      updatedStepsPreview,
      updatedWorkflowVersion,
    };
  } else {
    return { updatedSteps: null, updatedStepsPreview: null, updatedWorkflowVersion: null };
  }
};

/**
 * Helper to create a transition button on step if it doesn't exist, and return updated steps and steps preview
 */
export const ensureTransitionComponentExistsOnUiStep = (
  prevStep: InquiryTemplateVersionStepUi,
  steps: InquiryTemplateVersionStep[],
  stepsPreview: StepPreview[],
) => {
  const index = steps.findIndex((step) => step.name === prevStep.token);

  if (index >= 0) {
    const step = steps[index] as InquiryTemplateVersionStepUi;
    const stepPreview = stepsPreview[index] as StepUi;
    const hasTransitionComponent = uiStepAllComponentConfigs(step).some(
      isUiTransitionComponentConfig,
    );

    if (!hasTransitionComponent) {
      let updatedStep = step;
      let updatedStepPreview = stepPreview;
      const completeComponentPath = findComponentConfigPath(
        (component) => component.type === UiComponentConfigType.ButtonComplete,
        prevStep.config.components,
      );

      if (completeComponentPath == null) {
        updatedStep = set(
          ['config', 'components'],
          [...step.config.components, createTransitionComponent()],
          step,
        );

        updatedStepPreview = set(
          ['config', 'components'],
          [...stepPreview.config.components, createTransitionComponentPreview()],
          stepPreview,
        );
      } else {
        const completeComponentPreviewPath = completeComponentPath.map((pathComponent) =>
          pathComponent === 'config' ? 'attributes' : pathComponent,
        );

        // transition properties (to-step / workflow-version-id) are handled in stepsConfigTransitionsUpdated
        updatedStep = set(
          ['config', 'components', ...completeComponentPath],
          createTransitionComponent(),
          step,
        );

        updatedStepPreview = set(
          ['config', 'components', ...completeComponentPreviewPath],
          createTransitionComponentPreview(),
          stepPreview,
        );
      }

      return {
        updatedSteps: set([index], updatedStep, steps),
        updatedStepsPreview: set([index], updatedStepPreview, stepsPreview),
      };
    }
  }

  return {
    updatedSteps: steps,
    updatedStepsPreview: stepsPreview,
  };
};

/**
 * Updates ITE steps server config and previews when adding a new screen step or workflow.
 * Adds transition component if it doesn't exist on prev step if its a ui step
 * Transfers terminal state from prev step to next step
 */
export const stepsConfigPreviewsUpdated = (
  steps: InquiryTemplateVersionStep[],
  stepsPreview: StepPreview[],
  next: 'step' | 'workflow',
  nameOrToken: string,
  addStepConfig: AddStepConfig,
  omitUpdateConfig?: OmitUpdateConfig | null,
  componentName?: string,
  backStepName?: string,
) => {
  const { prevStep } = addStepConfig;
  let updatedSteps = steps;
  let updatedStepsPreview = stepsPreview;

  if (prevStep.type === UiStepType.Ui) {
    ({ updatedSteps, updatedStepsPreview } = ensureTransitionComponentExistsOnUiStep(
      prevStep,
      updatedSteps,
      updatedStepsPreview,
    ));
  }

  updatedSteps = stepsConfigTransitionsUpdated(
    updatedSteps,
    next,
    nameOrToken,
    addStepConfig,
    omitUpdateConfig,
    componentName,
    backStepName,
  );

  return {
    updatedSteps,
    updatedStepsPreview,
  };
};

/**
 * Updates ITE steps server config transition config when adding or removing a new screen step or workflow.
 *
 * Both prev and next step can separately be workflow tree steps,
 * but they should never simultaneously be workflow steps - this is
 * handled in an earlier function.
 *
 * Note: the steps param passed to this function already includes the inserted step.
 * This function's sole responsibility is updating the to_step and back_step values.
 * *
 * @returns template steps server config with prev and/or next step updated
 */
export const stepsConfigTransitionsUpdated = (
  steps: InquiryTemplateVersionStep[],
  next: 'step' | 'workflow',
  nameOrToken: string | undefined,
  addStepConfig: AddStepConfig,
  omitUpdateConfig?: OmitUpdateConfig | null,
  componentName?: string,
  backStepName?: string,
): InquiryTemplateVersionStep[] => {
  const { prevStep, nextStep } = addStepConfig;
  return steps.map((step) => {
    if (prevStep.token === step.name && isScreenStep(step) && !omitUpdateConfig?.omitNeighborNext) {
      // remove terminal statuses on prev step
      const stepToUpdate =
        isScreenStep(step) && nextStep == null
          ? stepWithUpdatedStatuses({
              step,
              statuses: step.config.statuses?.filter(({ terminal }) => !terminal) ?? [],
            })
          : step;
      return stepTransitionUpdated({
        step: stepToUpdate,
        transitionType: next,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        targetToken: nameOrToken,
        workflowErrorStepName: step.name,
        transitionComponentName: componentName,
      });
    } else if (
      next === 'step' &&
      nextStep?.token === step.name &&
      !omitUpdateConfig?.omitNeighborBack
    ) {
      // backStepName is set when deleting steps, otherwise use nameOrToken which is the added step name
      return isScreenStep(step) && step.type !== UiStepType.Loop && step.config['back-step']
        ? stepBackTransitionUpdated({ step, backStepTarget: backStepName ?? nameOrToken })
        : step;
    } else {
      return step;
    }
  });
};

/**
 * Updates steps and steps preview when removing a transition from a screen (setting next step to null)
 *
 * Handles setting terminal statuses and converting submit button to complete button if the screen
 * only has one transition
 * *
 * @returns template steps server config and steps preview with transitions removed
 */
export const stepsPreviewsWithRemovedTransition = (
  steps: InquiryTemplateVersionStep[],
  stepsPreview: StepPreview[],
  targetStep: InquiryTemplateVersionStep,
  statuses: InquiryTemplateVersionStepStatus[],
  componentName?: string,
): UpdatedStepsPreviewsWorkflows => {
  const index = steps.findIndex((step) => step.name === targetStep.name);
  const stepToUpdate = steps[index];

  if (isUiStepConfig(stepToUpdate)) {
    const stepPreviewToUpdate = stepsPreview.find(
      (stepPreview) => stepPreview.name === stepToUpdate.name,
    ) as StepUi;

    if (!stepPreviewToUpdate) {
      captureMessage(
        `Can't find step preview corresponding to step ${stepToUpdate.name}`,
        'warning',
      );
      return { updatedSteps: null, updatedStepsPreview: null };
    }

    const isTargetTransitionComponent = (componentConfig: UiComponentConfig): boolean => {
      return componentName != null
        ? isUiTransitionComponentConfig(componentConfig) && componentConfig.name === componentName
        : isUiTransitionComponentConfig(componentConfig);
    };
    const transitionComponentPath = findComponentConfigPath(
      isTargetTransitionComponent,
      stepToUpdate.config.components,
    );

    if (transitionComponentPath == null) {
      captureMessage(`Can't find transition component to remove in ${targetStep.name}`, 'warning');
      return { updatedSteps: null, updatedStepsPreview: null };
    }

    const transitionComponentPreviewPath = transitionComponentPath.map((pathComponent) =>
      pathComponent === 'config' ? 'attributes' : pathComponent,
    );

    const transitionComponent = get(transitionComponentPath, stepToUpdate.config.components);
    const transitionComponentPreview = get(
      transitionComponentPreviewPath,
      stepPreviewToUpdate.config.components,
    );

    const allTransitionComponents = uiStepAllComponentConfigs(stepToUpdate).filter(
      isUiTransitionComponentConfig,
    );

    if (allTransitionComponents.length === 1) {
      // if we are removing the only transition component, transform it into a button complete and transfer terminal statuses
      let updatedStep = set(
        ['config', 'components', ...transitionComponentPath],
        {
          ...transitionComponent,
          config: omit(
            ['workflow-version-id', 'workflow-on-error-step-name', 'to-step'],
            transitionComponent.config,
          ),
          type: UiComponentConfigType.ButtonComplete,
        },
        stepToUpdate,
      );

      // don't override statuses if our step already has statuses (e.g. is an initial step)
      if (!stepToUpdate.config.statuses?.length) {
        updatedStep = set(
          ['config', 'statuses'],
          statuses?.length > 0
            ? statuses
            : [
                {
                  name: 'completed',
                  initial: false,
                  terminal: true,
                },
              ],
          updatedStep,
        );
      }

      const updatedStepPreview = set(
        ['config', 'components', ...transitionComponentPreviewPath],
        {
          ...transitionComponentPreview,
          type: UiComponentConfigType.ButtonComplete,
        },
        stepPreviewToUpdate,
      );

      return {
        updatedSteps: set([index], updatedStep, steps),
        updatedStepsPreview: set([index], updatedStepPreview, stepsPreview),
      };
    } else {
      // if there is more than one transition component, just set next step to null for now,
      // note this will fail validations since we are directing to a null step
      // will need to add ability to delete the branch entirely as a separate action
      return {
        updatedSteps: stepsConfigTransitionsUpdated(
          steps,
          'step',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          null,
          {
            prevStep: targetStep,
            nextStep: null,
          },
          null,
          componentName,
        ),
        updatedStepsPreview: null,
      };
    }
  } else if (isCombinedStepConfig(stepToUpdate)) {
    const updatedStep = set(
      ['config', 'statuses'],
      statuses?.length > 0
        ? statuses
        : [
            {
              name: 'completed',
              initial: false,
              terminal: true,
            },
          ],
      stepToUpdate,
    );

    return {
      updatedSteps: set(
        [index],
        stepTransitionUpdated({
          step: updatedStep,
          transitionType: 'step',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          targetToken: null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          workflowErrorStepName: null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          transitionComponentName: null,
        }),
        steps,
      ),

      updatedStepsPreview: null,
    };
  } else {
    return {
      updatedSteps: null,
      updatedStepsPreview: null,
    };
  }
};

/**
 * Traverses ui step's component configs and concats
 * path to component config that matches predicateFn if one exists
 *
 * @returns optional path to component config to use in lodash set()
 */
export const findComponentConfigPath = (
  predicateFn: (componentConfig: UiComponentConfig) => boolean,
  componentConfig: UiComponentConfig[],
): string[] | undefined => {
  const matchedIndex = componentConfig.findIndex(predicateFn);
  if (matchedIndex !== -1) {
    return [matchedIndex.toString()];
  } else {
    for (let componentIndex = 0; componentIndex < componentConfig.length; componentIndex++) {
      const component = componentConfig[componentIndex];
      if (isUiParentComponentConfig(component)) {
        const {
          config: { children },
        } = component;
        const componentPath = findComponentConfigPath(predicateFn, children);
        if (componentPath) {
          return [componentIndex.toString(), 'config', 'children'].concat(componentPath);
        }
      }
    }
  }
};

/**
 * Applies the transformation function to the sequence of steps matching the target token and branch
 * Function is called with the array of steps corresponding to the workflow branch, as well as the index of the step matching the token
 * If branchIndex is passed, function is called on that branch of the conditional step matching the token, and the matched index is -1.
 */
export const transformWorkflowStepsMatchingTokenAndBranch = (
  transformationFn: (originalSteps: WorkflowStep[], matchedIndex: number) => WorkflowStep[],
  targetToken: string,
  branchIndex: number | null,
  steps: WorkflowStep[],
): WorkflowStep[] => {
  const index = steps.findIndex((step) => step.token === targetToken);
  // token exists on the current branch
  if (index >= 0) {
    const step = steps[index];
    if (step.type === WorkflowStepType.Conditional && branchIndex != null) {
      return set(
        [index, 'branches', branchIndex],
        transformationFn(step.branches[branchIndex], -1),
        steps,
      );
    } else {
      return transformationFn(steps, index);
    }
  } else {
    return steps.map((step) => {
      if (step.type === WorkflowStepType.Conditional) {
        return {
          ...step,
          branches: step.branches.map((branchSteps: WorkflowStep[]) =>
            transformWorkflowStepsMatchingTokenAndBranch(
              transformationFn,
              targetToken,
              branchIndex,
              branchSteps,
            ),
          ),
        };
      } else {
        return step;
      }
    });
  }
};

/**
 * Inserts the workflow step after the target step token
 * If a branch index is passed in, adds the step at the beginning of the branch on the conditional step
 * with the target token
 *
 * @returns workflow version's steps with inserted step
 */
export const insertWorkflowStepAfterTokenAndBranch = (
  steps: WorkflowStep[],
  targetToken: string,
  branchIndex: number | null,
  stepToInsert: WorkflowStep,
): WorkflowStep[] => {
  const index = steps.findIndex((step) => step.token === targetToken);
  if (index >= 0) {
    const step = steps[index];
    if (step.type === WorkflowStepType.Conditional && branchIndex != null) {
      return set(
        [index, 'branches', branchIndex],
        [stepToInsert, ...step.branches[branchIndex]],
        steps,
      );
    } else {
      return [...steps.slice(0, index + 1), stepToInsert, ...steps.slice(index + 1)];
    }
  } else {
    return steps.map((step) => {
      if (step.type === WorkflowStepType.Conditional) {
        return {
          ...step,
          branches: step.branches.map((branchSteps: WorkflowStep[]) =>
            insertWorkflowStepAfterTokenAndBranch(
              branchSteps,
              targetToken,
              branchIndex,
              stepToInsert,
            ),
          ),
        };
      } else {
        return step;
      }
    });
  }
};

/**
 * Creates a conditional step branching after the step referenced by the token
 * if branchIndex is passed in, first step in that branch will be first step in the newly created branch
 *
 * @returns workflow version's steps with inserted step
 */
export const createBranchAtToken = (
  steps: WorkflowStep[],
  targetToken: string,
  branchIndex: number | null,
): WorkflowStep[] => {
  const index = steps.findIndex((step) => step.token === targetToken);
  if (index >= 0) {
    const step = steps[index];
    if (step.type === WorkflowStepType.Conditional && branchIndex != null) {
      return set(
        [index, 'branches', branchIndex],
        [createConditionalStepWithStepsInFirstBranch(step.branches[branchIndex])],
        steps,
      );
    } else {
      return [
        ...steps.slice(0, index + 1),
        createConditionalStepWithStepsInFirstBranch(steps.slice(index + 1)),
      ];
    }
  } else {
    return steps.map((step) => {
      if (step.type === WorkflowStepType.Conditional) {
        return {
          ...step,
          branches: step.branches.map((branchSteps: WorkflowStep[]) =>
            createBranchAtToken(branchSteps, targetToken, branchIndex),
          ),
        };
      } else {
        return step;
      }
    });
  }
};

/**
 * Creates a conditional step branching before the next step transition in the branch referenced by the token
 *
 * @returns workflow version's steps with inserted step
 */
export const createBranchBeforeStepTransition = (
  steps: WorkflowStep[],
  targetToken: string,
): WorkflowStep[] => {
  const referencedStep = steps.find((step) => step.token === targetToken);
  if (referencedStep) {
    // action nodes are drawn before the step transition in flow editor even if they come after in the workflow.
    // To visually branch right before the step, we have to move all actions coming after the transition before,
    // and add just the transition step to the new conditional branch
    const transitionStepIndex = steps.findIndex(isUpdateInquiryNextStepActionStep);
    if (transitionStepIndex >= 0) {
      return [
        ...steps.slice(0, transitionStepIndex),
        ...steps.slice(transitionStepIndex + 1),
        createConditionalStepWithStepsInFirstBranch([steps[transitionStepIndex]]),
      ];
    } else {
      return steps;
    }
  } else {
    return steps.map((step) => {
      if (step.type === WorkflowStepType.Conditional) {
        return {
          ...step,
          branches: step.branches.map((branchSteps: WorkflowStep[]) =>
            createBranchBeforeStepTransition(branchSteps, targetToken),
          ),
        };
      } else {
        return step;
      }
    });
  }
};

/**
 * Used in WorkflowStepPanel to find redux form path to current conditional step
 *
 * @returns string dot-notated path to workflow step with target token
 */
export const getWorkflowStepPath = (
  token: string,
  steps: WorkflowStep[],
  base?: string,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
): string => {
  for (let i = 0; i < steps.length; i++) {
    const path = base ? `${base}.${i}` : `${i}`;
    const step = steps[i];
    if (step.token === token) {
      return path;
    }
    if (step.type === WorkflowStepType.Conditional) {
      const branchPath = step.branches
        .map((branch, idx) => getWorkflowStepPath(token, branch, idx.toString()))
        .filter(Boolean)[0];
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      return branchPath ? `${path}.branches.${branchPath}` : null;
    }
  }
};

/**
 * Function used to update the 'tree' data used in flow editor - stepsConfig stepsPreview and workflowVersions.
 * when removing a screen node.
 *
 * @returns updated tree data to be dispatched to redux store
 */
export const stepsWorkflowsPreviewsWithDeletedScreenNode = ({
  flowTreeData,
  deleteStepConfig,
}: {
  flowTreeData: FlowTreeData;
  deleteStepConfig: DeleteStepConfig;
}): UpdatedStepsPreviewsWorkflows => {
  const { steps, stepsPreview, workflowVersionMap } = flowTreeData;
  const { branchIndex, currentStep, nextStep, prevStep } = deleteStepConfig;

  if (currentStep.type === 'workflow') {
    captureMessage(
      `Attempting to delete itv node but was passed workflow node ${currentStep.token}`,
      'warning',
    );

    return {
      updatedSteps: null,
      updatedStepsPreview: null,
      updatedWorkflowVersion: null,
    };
  }

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  if (isScreenStep(prevStep)) {
    const componentName = getTransitionComponentName(prevStep, branchIndex);
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    if (isNonWorkflowNextStep(nextStep)) {
      const updatedSteps = stepsConfigTransitionsUpdated(
        steps,
        'step',
        nextStep.name,
        {
          prevStep,
          nextStep,
        },
        null,
        componentName,
        prevStep.name,
      );

      return {
        updatedSteps,
        updatedStepsPreview: null,
        updatedWorkflowVersion: null,
      };
    } else if (nextStep?.type === 'workflow') {
      const updatedSteps = stepsConfigTransitionsUpdated(
        steps,
        'workflow',
        nextStep.workflowVersionId,
        {
          prevStep,
          nextStep,
        },
        null,
        componentName,
      );

      return {
        updatedSteps,
        updatedStepsPreview: null,
        updatedWorkflowVersion: null,
      };
    } else if (nextStep == null) {
      const { updatedSteps, updatedStepsPreview } = stepsPreviewsWithRemovedTransition(
        steps,
        stepsPreview,
        prevStep,
        (currentStep as InquiryTemplateVersionStep).config?.statuses,
        componentName,
      );

      return {
        updatedSteps,
        updatedStepsPreview,
        updatedWorkflowVersion: null,
      };
    }
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  } else if (prevStep.type === 'workflow') {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const workflowVersionToUpdate = workflowVersionMap[prevStep.workflowVersionId];
    if (nextStep?.type === 'workflow') {
      const workflowStepsToJoin =
        workflowVersionMap[nextStep.workflowVersionId].attributes['steps-config'];

      const updatedWorkflowVersion = set(
        ['attributes', 'steps-config'],
        transformWorkflowStepsMatchingTokenAndBranch(
          (steps) => {
            return [
              // remove step transition
              ...steps.filter((step) => !isUpdateInquiryNextStepActionStep(step)),
              // append steps from next workflow
              ...workflowStepsToJoin,
            ];
          },
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          prevStep.workflowStep.token,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          branchIndex,
          workflowVersionToUpdate.attributes['steps-config'],
        ),
        workflowVersionToUpdate,
      );

      return {
        updatedSteps: null,
        updatedStepsPreview: null,
        updatedWorkflowVersion,
      };
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    } else if (isNonWorkflowNextStep(nextStep)) {
      const updatedWorkflowVersion = set(
        ['attributes', 'steps-config'],
        transformWorkflowStepsMatchingTokenAndBranch(
          (steps) => workflowStepsNextUpdated(steps, nextStep?.name),
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          prevStep.workflowStep.token,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          branchIndex,
          workflowVersionToUpdate.attributes['steps-config'],
        ),
        workflowVersionToUpdate,
      );

      return {
        updatedSteps: null,
        updatedStepsPreview: null,
        updatedWorkflowVersion,
      };
    } else if (nextStep == null) {
      const updatedWorkflowVersion = set(
        ['attributes', 'steps-config'],
        transformWorkflowStepsMatchingTokenAndBranch(
          (steps) => steps.filter((step) => !isUpdateInquiryNextStepActionStep(step)),
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          prevStep.workflowStep.token,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          branchIndex,
          workflowVersionToUpdate.attributes['steps-config'],
        ),
        workflowVersionToUpdate,
      );

      return {
        updatedSteps: null,
        updatedStepsPreview: null,
        updatedWorkflowVersion,
      };
    }
  }

  return { updatedSteps: null, updatedStepsPreview: null, updatedWorkflowVersion: null };
};

/**
 * Function used to update the 'tree' data used in flow editor - stepsConfig stepsPreview and workflowVersions.
 * when removing a workflow node.
 *
 * @returns updated tree data to be dispatched to redux store
 */
export const stepsWorkflowsPreviewsWithDeletedWorkflowNode = (
  steps: InquiryTemplateVersionStep[],
  stepsPreview: StepPreview[],
  workflowVersionMap: WorkflowVersionMap,
  deleteStepConfig: DeleteStepConfig,
): UpdatedStepsPreviewsWorkflows => {
  const { branchIndex, currentStep, nextStep, prevStep } = deleteStepConfig;

  if (isScreenStep(currentStep) || currentStep.type === UiStepType.Complete) {
    captureMessage(
      `Attempting to delete workflow node but was passed screen or complete step ${currentStep.token}`,
      'warning',
    );

    return {
      updatedSteps: null,
      updatedStepsPreview: null,
      updatedWorkflowVersion: null,
    };
  }

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  if (isScreenStep(prevStep) && (nextStep == null || nextStep.type !== 'workflow')) {
    const componentName = getTransitionComponentName(prevStep, branchIndex);
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    if (isNonWorkflowNextStep(nextStep)) {
      const updatedSteps = stepsConfigTransitionsUpdated(
        steps,
        'step',
        nextStep.name,
        {
          prevStep,
          nextStep,
        },
        null,
        componentName,
        prevStep.name,
      );

      return {
        updatedSteps,
        updatedStepsPreview: null,
        updatedWorkflowVersion: null,
      };
    } else if (nextStep == null) {
      const { updatedSteps, updatedStepsPreview } = stepsPreviewsWithRemovedTransition(
        steps,
        stepsPreview,
        prevStep,
        [],
        componentName,
      );

      return {
        updatedSteps,
        updatedStepsPreview,
        updatedWorkflowVersion: null,
      };
    }
  } else {
    const workflowVersionToUpdate = workflowVersionMap[currentStep.workflowVersionId];
    const updatedWorkflowVersion = set(
      ['attributes', 'steps-config'],
      transformWorkflowStepsMatchingTokenAndBranch(
        (steps) => {
          if (currentStep.workflowStep.type === WorkflowStepType.Conditional) {
            // remove conditional step from its containing branch, and append its first
            // branch actions, moving step transition action to the front if applicable
            const firstBranchActions =
              getConditionalStepForToken(currentStep.workflowStep.token, workflowVersionToUpdate)
                ?.branches?.[0] ?? [];

            return [
              ...steps.filter((step) => !(step.token === currentStep.workflowStep.token)),
              ...firstBranchActions,
            ];
          } else {
            // we are deleting an action step, the prior step must be a conditional step branch,
            // and next step is either a conditional step or a screen step, we are fine deleting
            // all actions in this branch that are not conditional or step transition actions
            return steps.filter(
              (step) =>
                step.type === WorkflowStepType.Conditional ||
                isUpdateInquiryNextStepActionStep(step),
            );
          }
        },
        currentStep.workflowStep.token,
        null,
        workflowVersionToUpdate.attributes['steps-config'],
      ),
      workflowVersionToUpdate,
    );

    return {
      updatedSteps: null,
      updatedStepsPreview: null,
      updatedWorkflowVersion,
    };
  }

  return { updatedSteps: null, updatedStepsPreview: null, updatedWorkflowVersion: null };
};

/**
 * Filters out deleted screen step if it's not referenced
 * by any remaining connected steps or workflow versions
 * @param deletedStepName
 * @param steps updated steps data
 * @param workflowVersionMap updated map of connected workflow versions
 */
export const stepsWithDeletedScreenHandled = (
  deletedStepName: string,
  steps: InquiryTemplateVersionStep[],
  workflowVersionMap: WorkflowVersionMap,
): InquiryTemplateVersionStep[] => {
  const filteredSteps = steps.filter((step) => step.name !== deletedStepName);
  return isStepConnected({
    stepName: deletedStepName,
    steps: filteredSteps,
    workflowVersionMap,
  })
    ? steps
    : filteredSteps;
};

/**
 * Determines whether a screen step is referenced
 * by any connected steps or workflow versions
 * @param stepName
 * @param steps updated steps data
 * @param workflowVersionMap updated map of connected workflow versions
 * @returns whether the step has remaining connections to tree
 */
export const isStepConnected = ({
  stepName,
  steps,
  workflowVersionMap,
}: {
  stepName: string;
  steps: InquiryTemplateVersionStep[];
  workflowVersionMap: WorkflowVersionMap;
}): boolean => {
  const stepsNextStepNames = stepsToStepTargets(steps);
  const stepsWorkflowErrorNames = stepsWorkflowErrorTargets(steps);
  const workflowsNextStepNames = Object.values(workflowVersionMap).flatMap((workflowVersion) =>
    workflowNextStepNames(workflowVersion.attributes['steps-config']),
  );
  return [...stepsNextStepNames, ...stepsWorkflowErrorNames, ...workflowsNextStepNames].includes(
    stepName,
  );
};

export const findStepInTree = (
  stepsTree: CombinedStepForTree[],
  nameOrToken: string,
): CombinedStepForTree => {
  let stepsQueue = [...stepsTree];
  while (stepsQueue.length > 0) {
    const step = stepsQueue.pop();
    if (
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      (step.type === 'workflow' && step.token === nameOrToken) ||
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      (step.type !== 'workflow' && step.name === nameOrToken)
    ) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      return step;
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    } else if (step.branches != null) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      stepsQueue = stepsQueue.concat(...step.branches);
    }
  }
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return null;
};

export const isTreeStepWorkflowConditional = (
  treeStep: CombinedStepForTree,
): treeStep is LinkedWorkflowStep => {
  return (
    treeStep.type === 'workflow' && treeStep?.workflowStep?.type === WorkflowStepType.Conditional
  );
};
